import { useEffect, useMemo, useRef, useState } from 'react'
import { create } from 'zustand'
import { UseQueryResult } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { SparqlStatus } from '@/helpers/use-sparql-query'
import { OpaqueSparqlResults } from '@/helpers/sparqlResultHelpers'
import { resolveTemplateString } from '@/helpers/resolve-template-string'
import { PublishedDict } from '@/redux/pubsub'

export type WidgetQueryStatus = {
  status: SparqlStatus
  data: OpaqueSparqlResults | null
  rowCount: number | null
  resultVars: string[]
  durationMs: number | null
  error: string | null
  /** Response body of a failed request, when the endpoint sent one */
  errorDetail: string | null
  /** The query as configured, with placeholders */
  template: string | null
  /** The query as it was sent, with placeholders resolved */
  resolvedQuery: string | null
  endpoint: string
  /** Placeholders without a value, which keep the query from running */
  missingPlaceholders: string[]
  /** Topic values the placeholders resolve against */
  published: PublishedDict
  /** Resolves the widget's query with extra placeholder values, `null` while still unresolved */
  resolveWith: (values: Record<string, string>) => string | null
  rerun: () => void
}

/** Live status of each rendered widget's main query, keyed by widget id, so the configurator can show it. */
export const useWidgetQueryStatusStore = create<Record<string, WidgetQueryStatus>>()(() => ({}))

export function useReportWidgetQueryStatus(params: {
  widgetId: string | undefined
  status: SparqlStatus
  queryResult: UseQueryResult<OpaqueSparqlResults>
  template: string | undefined
  resolvedQuery: string | null
  published: PublishedDict
  endpoint: string
  resolve: (template: string, published: PublishedDict) => string | null
}) {
  const { widgetId, status, queryResult, template, resolvedQuery, published, endpoint, resolve } = params
  const durationMs = useFetchDuration(queryResult.fetchStatus)
  const { data, error, refetch } = queryResult

  const missingPlaceholders = useMemo(
    () => template ? findMissingPlaceholders(template, published) : [],
    [ template, published ]
  )

  useEffect(function publishStatus() {
    if (!widgetId) return
    useWidgetQueryStatusStore.setState({
      [widgetId]: {
        status,
        data: data ?? null,
        rowCount: data?.results?.bindings?.length ?? null,
        resultVars: data?.head?.vars ?? [],
        durationMs,
        error: error?.message ?? null,
        errorDetail: describeErrorResponse(error),
        template: template ?? null,
        resolvedQuery: resolvedQuery || null,
        endpoint,
        missingPlaceholders,
        published,
        resolveWith: values => template ? resolve(template, { ...published, ...values }) : null,
        // refetch() runs even a disabled query, which would send it with its placeholders unresolved
        rerun: () => { if (status !== "disabled") void refetch() },
      }
    })
  }, [ widgetId, status, data, durationMs, error, resolvedQuery, endpoint, missingPlaceholders, template, published, resolve, refetch ])

  useEffect(function removeStatusOnUnmount() {
    if (!widgetId) return
    return () => {
      useWidgetQueryStatusStore.setState(st => {
        const { [widgetId]: _removed, ...rest } = st
        return rest
      }, true)
    }
  }, [ widgetId ])
}

/** The topic a placeholder like `topic||fallbackTopic::fallbackValue` reads first */
export function placeholderTopic(placeholder: string) {
  return placeholder.split('||')[0].split('::')[0].trim()
}

/** Every placeholder in the query with its current value, null when it has none */
export function resolvePlaceholders(template: string, published: PublishedDict): { placeholder: string, value: unknown }[] {
  const found = new Map<string, unknown>()
  const filled: Record<string, unknown> = { ...published }
  // resolving stops at the first placeholder without a value, so fill each one in and look again
  for (let attempt = 0; attempt < 20; attempt++) {
    let firstMissing: string | null = null
    resolveTemplateString(template, filled as PublishedDict, (placeholder, value) => {
      if (!found.has(placeholder)) found.set(placeholder, value ?? null)
      if (value == null && firstMissing == null) firstMissing = placeholder
    })
    if (firstMissing == null) break
    filled[placeholderTopic(firstMissing)] = "_"
  }
  return [ ...found ].map(([ placeholder, value ]) => ({ placeholder, value }))
}

function findMissingPlaceholders(template: string, published: PublishedDict) {
  const missing: string[] = []
  const filled: Record<string, unknown> = { ...published }
  // resolving stops at the first placeholder without a value, so fill each one in and look again
  for (let attempt = 0; attempt < 20; attempt++) {
    let firstMissing: string | null = null
    resolveTemplateString(template, filled as PublishedDict, (placeholder, value) => {
      if (value == null && firstMissing == null) firstMissing = placeholder
    })
    if (firstMissing == null) break
    missing.push(firstMissing)
    filled[placeholderTopic(firstMissing)] = "_"
  }
  return missing
}

function describeErrorResponse(error: Error | null): string | null {
  if (!isAxiosError(error)) return null
  const body = error.response?.data
  if (body == null || body === "") return null
  return typeof body === "string" ? body : JSON.stringify(body, null, 2)
}

function useFetchDuration(fetchStatus: UseQueryResult['fetchStatus']) {
  const startRef = useRef<number | null>(null)
  const [ durationMs, setDurationMs ] = useState<number | null>(null)

  useEffect(function measureFetch() {
    if (fetchStatus === 'fetching') {
      startRef.current = performance.now()
    } else if (startRef.current != null) {
      setDurationMs(Math.round(performance.now() - startRef.current))
      startRef.current = null
    }
  }, [ fetchStatus ])

  return durationMs
}
